/**
 * ChatView — the live Argo agent plus saved conversation history.
 *
 * The "Argo agent" thread is a working chat: you type a natural-language
 * instruction ("add a prospect", "create a proposal for X", "move Acme to
 * Deal Signed") and the agent interprets it into one or more *proposed*
 * actions. Nothing writes to the store until you click Confirm on a card
 * (confirm-each-write). Interpretation runs through `agentBackend`, which
 * resolves to the hosted Argo agent-worker when configured (VITE_AGENT_API_URL)
 * and the deterministic local parser otherwise — this file's UX is identical
 * either way (see src/lib/agent/backend.ts).
 *
 * The conversation machinery (send/confirm/dismiss/revert/auto-run) lives in
 * the shared useAgentConversation hook, and the bubbles/cards in the shared
 * AgentMessages components — both also power the docked AskArgoDrawer, so
 * the two surfaces can never drift apart on the governance contract.
 *
 * The remaining threads are static history kept for context/demo.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Bot,
  Brain,
  CircleDashed,
  CornerDownLeft,
  Loader2,
  Lock,
  MessageSquarePlus,
  Paperclip,
  Search,
  Sparkles,
  X,
  Zap,
} from 'lucide-react';
import { GlassPanel } from '../components/GlassPanel';
import { MessageBubble } from '../components/AgentMessages';
import { C } from '../lib/tokens';
import type { AgentPref } from '../lib/db/types';
import { isConfigured as workerConfigured } from '../lib/agent/argoWorker';
import {
  forgetAllPrefs,
  forgetPref,
  subscribePrefs,
  MEMORY_THRESHOLD,
} from '../lib/agent/memory';
import { useAuth } from '../lib/auth/AuthProvider';
import {
  fmtTime,
  useAgentConversation,
  type ChatMessage,
} from '../lib/agent/useAgentConversation';
import { quickSuggestionsFor } from '../lib/agent/quickSuggestions';

const AGENT_THREAD_ID = 'agent';

interface Thread {
  id: string;
  title: string;
  preview: string;
  when: string;
  unread?: number;
  live?: boolean;
}

// No static history threads: chat threads aren't persisted yet, so the only
// real conversation is the live Argo agent. We deliberately show just that
// rather than seed fake "past conversations" — honesty is the product's spine,
// and demo threads next to real features read as overselling. When thread
// persistence ships, populate this from the store.
const historyThreads: Thread[] = [];

const GREETING: ChatMessage = {
  id: 'greet',
  who: 'argo',
  body:
    'Hi — I’m the Argo agent. Tell me what to do across your CRM and I’ll propose the writes for you to confirm.\n\nTry: “Add prospect Jane Doe at Acme, deal worth $48k”, “Create a proposal for Argo IQ”, or “Move Argus to Deal Signed”.',
  when: fmtTime(),
};

/**
 * One-click starter prompts shown above the composer on a fresh agent
 * thread. Each maps onto an intent the backend already understands, so the
 * chat is discoverable instead of a blank box ("easy to interact with").
 */
const QUICK_PROMPTS: string[] = [
  'What needs my attention today?',
  'Add prospect Jane Doe at Acme, deal worth $48k',
  'Create a proposal for Argo IQ',
  'Follow up with Jane Doe next week',
  'Open a ticket to fix the billing export (p1)',
];

/** Starter prompts when the CRM is empty — first-run setup, not data Q&A. */
const SETUP_PROMPTS: string[] = [
  'Add prospect Jane Doe at Acme, deal worth $48k',
  'Open a ticket to fix the billing export (p1)',
  'What can you do?',
];

/**
 * Agentic first-run onboarding: shown as the latest agent bubble whenever
 * the CRM has no contacts or prospects. Paste-import (multi-line paste in
 * the composer auto-sends; the backend reads it as a roster and proposes
 * one confirmable create per line) means setup is a conversation, not a
 * form — and the bubble disappears the moment the first import lands.
 */
const SETUP_MESSAGE_BODY =
  'Your CRM is empty — let’s fix that right here, no forms.\n\n' +
  'Paste your book of business straight into the composer, one contact per line:\n\n' +
  'Jane Doe, Acme, jane@acme.com, $48k\n' +
  'Raj Patel at Brightline, raj@brightline.io\n\n' +
  'Start the paste with “customers:” to import as customers instead of prospects. ' +
  'I’ll turn every line into a card and you confirm exactly what gets written.';

interface ChatViewProps {
  /** A query handed in from the ⌘K command palette. When the nonce
   *  changes, the agent thread auto-sends the text. */
  initialQuery?: { text: string; nonce: number } | null;
}

export function ChatView({ initialQuery }: ChatViewProps = {}) {
  const [active, setActive] = useState(AGENT_THREAD_ID);
  const [composer, setComposer] = useState('');
  const conv = useAgentConversation(GREETING);
  const { messages, busy, crmEmpty } = conv;
  // Agent memory — learned preferences shown in the header popover and
  // injected into the worker context on each interpret().
  const { user } = useAuth();
  const canMutate = user?.role !== 'viewer';
  const [prefs, setPrefs] = useState<AgentPref[]>([]);
  const [showMemory, setShowMemory] = useState(false);
  // Filters the thread sidebar by title/preview. Wired to the sidebar search
  // box (previously a dead input that displayed but filtered nothing).
  const [threadQuery, setThreadQuery] = useState('');

  useEffect(() => subscribePrefs(setPrefs), []);

  const threads = useMemo<Thread[]>(
    () => [
      {
        id: AGENT_THREAD_ID,
        title: 'Argo agent',
        preview: 'Ask me to update your CRM — I’ll confirm before writing.',
        when: 'now',
        live: true,
      },
      ...historyThreads,
    ],
    [],
  );

  // Sidebar search: filter threads by title/preview, case-insensitive. The
  // live agent thread is always pinned in so a search can never strand the
  // operator away from the working chat.
  const visibleThreads = useMemo<Thread[]>(() => {
    const q = threadQuery.trim().toLowerCase();
    if (!q) return threads;
    return threads.filter(
      (t) =>
        t.id === AGENT_THREAD_ID ||
        t.title.toLowerCase().includes(q) ||
        t.preview.toLowerCase().includes(q),
    );
  }, [threads, threadQuery]);

  const isAgent = active === AGENT_THREAD_ID;
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, isAgent]);

  /** Composer-aware send: clears the box and refuses non-agent threads.
   *  The actual interpret/propose loop lives in the shared hook. */
  const send = async (override?: string) => {
    const text = (override ?? composer).trim();
    if (!text || busy) return;
    // Composer sends only count on the live agent thread; an override
    // (from the ⌘K palette) always targets the agent.
    if (!override && !isAgent) return;
    setComposer('');
    await conv.send(text);
  };

  // Auto-send a query handed in from the ⌘K palette. Tracked by nonce so
  // the same text can be asked twice; switches to the live agent thread.
  const lastNonce = useRef<number | null>(null);
  useEffect(() => {
    if (!initialQuery || initialQuery.nonce === lastNonce.current) return;
    lastNonce.current = initialQuery.nonce;
    setActive(AGENT_THREAD_ID);
    void send(initialQuery.text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuery]);

  /** Start a fresh agent conversation: clear the transcript back to the
   *  greeting and focus the live thread. (The "New chat" button was a dead
   *  affordance before this.) */
  const newChat = () => {
    setActive(AGENT_THREAD_ID);
    conv.reset();
    setComposer('');
  };

  const activeThread = threads.find((t) => t.id === active);

  // Deterministic quick-suggestion chips for the entity the thread last
  // referenced (person/account). Each chip PREFILLS a pending proposal via
  // conv.propose — it never runs anything; the confirm card still gates the
  // write. Recomputed per turn: `conv.context` is a live ref target, and its
  // `recent` pointer only moves when a new message lands.
  const suggestions = useMemo(
    () => quickSuggestionsFor(conv.context.recent, conv.context),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [messages],
  );

  return (
    <div className="app-container" style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 16, alignItems: 'flex-start' }}>
      {/* Threads sidebar */}
      <GlassPanel style={{ padding: '14px 12px', position: 'sticky', top: 'calc(var(--topmenu-h) + 24px)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 8px 12px' }}>
          <h3 className="t-section-title" style={{ margin: 0 }}>Chats</h3>
          <button
            type="button"
            className="glass-btn"
            style={{ padding: '4px 8px', cursor: 'pointer' }}
            onClick={newChat}
            title="New chat — start a fresh agent conversation"
          >
            <MessageSquarePlus size={13} />
          </button>
        </div>
        <div style={{ position: 'relative', padding: '0 4px 10px' }}>
          <Search size={13} style={{ position: 'absolute', top: '50%', left: 14, transform: 'translateY(-50%)', color: C.textDim, marginTop: -5 }} />
          <input
            className="glass-input"
            placeholder="Search…"
            value={threadQuery}
            onChange={(e) => setThreadQuery(e.target.value)}
            style={{ paddingLeft: 32, fontSize: 12 }}
          />
        </div>
        <div style={{ display: 'grid', gap: 4 }}>
          {visibleThreads.length === 0 && (
            <div className="t-meta" style={{ padding: '8px 12px' }}>
              No chats match “{threadQuery}”.
            </div>
          )}
          {visibleThreads.map((t) => {
            const isActive = active === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setActive(t.id)}
                className="glass-row"
                style={{
                  borderRadius: 12, border: 'none',
                  background: isActive ? 'rgba(255,0,255,0.1)' : 'transparent',
                  boxShadow: isActive ? 'inset 3px 0 0 rgba(255,0,255,0.55)' : 'none',
                  padding: '10px 12px',
                  cursor: 'pointer', textAlign: 'left',
                  display: 'block',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span className="t-card-title" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}>
                    {t.live && <Bot size={13} color={C.magentaSoft} />}
                    {t.title}
                  </span>
                  <span className="t-meta" style={{ marginLeft: 8 }}>{t.when}</span>
                </div>
                <div className="t-meta" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {t.preview}
                </div>
                {t.live && (
                  <span className="glass-pill glass-pill--green" style={{ marginTop: 6 }}>
                    Live
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </GlassPanel>

      {/* Conversation */}
      <GlassPanel style={{ padding: 0, display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - var(--topmenu-h) - 96px)' }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 24px', borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}>
          <div>
            <h3 className="t-section-title" style={{ margin: 0 }}>{activeThread?.title ?? 'Chat'}</h3>
            <span className="t-meta">{isAgent ? 'Argo AI · confirm-each-write' : 'Saved conversation'}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isAgent && prefs.length > 0 && (
              <button
                type="button"
                className="glass-pill"
                onClick={() => setShowMemory((v) => !v)}
                title={
                  'Agent memory — preferences learned from dismissed/reverted proposals. ' +
                  (workerConfigured()
                    ? 'Injected into the Claude worker context on every message.'
                    : 'Will be injected into the Claude worker context once one is connected; the local parser ignores it.')
                }
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
              >
                <Brain size={11} /> memory · {prefs.length}
              </button>
            )}
            {isAgent && (
              <span
                className={workerConfigured() ? 'glass-pill glass-pill--green' : 'glass-pill'}
                title={
                  workerConfigured()
                    ? 'Chat is interpreted by the real Claude worker (VITE_AGENT_API_URL).'
                    : 'Simulation mode — set VITE_AGENT_API_URL to connect the real Claude worker.'
                }
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                {workerConfigured() ? <Zap size={11} /> : <CircleDashed size={11} />}
                {workerConfigured() ? 'Connected to Claude worker' : 'Simulation mode'}
              </span>
            )}
            <span className={`glass-pill ${isAgent ? 'glass-pill--green' : ''}`}>{isAgent ? 'Live' : 'History'}</span>
          </div>
        </div>

        {/* Agent memory panel — what the agent has learned, with the
            operator in control of forgetting any of it. Honest labeling:
            notes below the threshold are shown as "forming" and are not
            injected yet. */}
        {isAgent && showMemory && prefs.length > 0 && (
          <div
            style={{
              padding: '12px 24px',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
              display: 'grid',
              gap: 8,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <span className="t-eyebrow" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Brain size={12} /> Agent memory
              </span>
              {canMutate && (
                <button
                  type="button"
                  className="glass-btn"
                  style={{ padding: '2px 8px' }}
                  onClick={() => void forgetAllPrefs()}
                >
                  Forget all
                </button>
              )}
            </div>
            {[...prefs]
              .sort((a, b) => b.count - a.count)
              .map((p) => (
                <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span
                    className="t-meta"
                    style={{ flex: 1, minWidth: 0, color: p.count >= MEMORY_THRESHOLD ? C.text : C.textMuted }}
                  >
                    {p.note}
                  </span>
                  <span
                    className="glass-pill"
                    style={{ flex: '0 0 auto' }}
                    title={
                      p.count >= MEMORY_THRESHOLD
                        ? 'Active — injected into the worker context.'
                        : `Forming — needs ${MEMORY_THRESHOLD}+ occurrences before it is injected.`
                    }
                  >
                    {p.count >= MEMORY_THRESHOLD ? 'active' : 'forming'} · {p.count}×
                  </span>
                  {canMutate && (
                    <button
                      type="button"
                      className="glass-btn"
                      style={{ padding: '2px 8px', flex: '0 0 auto' }}
                      onClick={() => void forgetPref(p.id)}
                      aria-label="Forget this preference"
                    >
                      <X size={11} />
                    </button>
                  )}
                </div>
              ))}
          </div>
        )}

        <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '24px', display: 'grid', gap: 16, alignContent: 'start' }}>
          {isAgent ? (
            <>
              {messages.map((m) => (
                <MessageBubble
                  key={m.id}
                  message={m}
                  people={conv.context.people}
                  brainFacts={conv.context.brainFacts}
                  canMutate={canMutate}
                  onConfirm={conv.confirm}
                  onDismiss={conv.dismiss}
                  onRevert={conv.revert}
                  onConfirmAll={conv.confirmAll}
                  onDismissAll={conv.dismissAll}
                  onHandleAll={(msg) =>
                    conv.propose(
                      `Queued all ${(msg.actions ?? []).length} — approve what you want done.`,
                      (msg.actions ?? []).map((p) => p.action),
                    )
                  }
                />
              ))}
              {crmEmpty && (
                <MessageBubble
                  message={{ id: 'setup', who: 'argo', body: SETUP_MESSAGE_BODY, when: fmtTime() }}
                  people={conv.context.people}
                  brainFacts={conv.context.brainFacts}
                  canMutate={canMutate}
                  onConfirm={conv.confirm}
                  onDismiss={conv.dismiss}
                  onRevert={conv.revert}
                  onConfirmAll={conv.confirmAll}
                  onDismissAll={conv.dismissAll}
                />
              )}
            </>
          ) : (
            <div className="t-meta" style={{ padding: '40px 0', textAlign: 'center' }}>
              {activeThread?.preview}
              <div style={{ marginTop: 10 }}>
                Open <strong>Argo agent</strong> to give live instructions.
              </div>
            </div>
          )}
        </div>

        {/* Composer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          {isAgent && !canMutate && (
            <div
              className="t-meta"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 10,
                padding: '8px 12px',
                borderRadius: 10,
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.10)',
                color: C.textMuted,
              }}
            >
              <Lock size={12} />
              Read-only access — viewers can follow along, but only editors can send commands or run Argo's writes.
            </div>
          )}
          {isAgent && canMutate && messages.length <= 1 && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
                marginBottom: 10,
              }}
            >
              {(crmEmpty ? SETUP_PROMPTS : QUICK_PROMPTS).map((prompt) => (
                <button
                  key={prompt}
                  className="glass-pill"
                  onClick={() => void send(prompt)}
                  disabled={busy}
                  style={{
                    cursor: busy ? 'default' : 'pointer',
                    border: '1px solid rgba(255,0,255,0.22)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                  }}
                  title="Send this prompt"
                >
                  <Sparkles size={11} color={C.magentaSoft} />
                  {prompt}
                </button>
              ))}
            </div>
          )}
          <GlassPanel variant="subtle" animate={false} style={{ padding: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button className="glass-btn" style={{ padding: 8, background: 'transparent', border: 'none' }}>
                <Paperclip size={14} />
              </button>
              <input
                className="glass-input"
                placeholder={!isAgent ? 'Open Argo agent to send a command…' : !canMutate ? 'Read-only access — editors only' : 'Tell Argo what to do — e.g. “Create a proposal for Argo IQ”'}
                value={composer}
                disabled={!isAgent || busy || !canMutate}
                onChange={(e) => setComposer(e.target.value)}
                onPaste={(e) => {
                  // A single-line <input> silently flattens pasted newlines,
                  // which would destroy a pasted roster. Intercept multi-line
                  // pastes and send them whole — the backend reads them as a
                  // roster import (or falls through to normal parsing).
                  if (!isAgent || busy || !canMutate) return;
                  const pasted = e.clipboardData.getData('text');
                  if (pasted.trim().includes('\n')) {
                    e.preventDefault();
                    void send(pasted);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void send();
                  }
                }}
                style={{ background: 'transparent', border: 'none', boxShadow: 'none', flex: 1 }}
              />
              <button className="glass-btn" style={{ padding: 8, background: 'transparent', border: 'none' }}>
                <Sparkles size={14} color={C.magentaSoft} />
              </button>
              <button
                className="glass-btn glass-btn--primary"
                style={{ padding: '8px 12px' }}
                onClick={() => void send()}
                disabled={!isAgent || busy || !composer.trim() || !canMutate}
              >
                {busy ? <Loader2 size={13} style={{ animation: 'auth-spin 0.8s linear infinite' }} /> : <>Send <CornerDownLeft size={13} /></>}
              </button>
            </div>
          </GlassPanel>
          {isAgent && canMutate && suggestions.length > 0 && (
            <div
              style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}
              aria-label="Quick actions"
            >
              {suggestions.map((s) => (
                <button
                  key={s.label}
                  className="glass-pill"
                  disabled={busy}
                  onClick={() =>
                    conv.propose('One-click suggestion — confirm to run it:', [s.action])
                  }
                  style={{
                    cursor: busy ? 'default' : 'pointer',
                    border: '1px solid rgba(255,0,255,0.22)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                  }}
                  title="Prefill this as a proposal — nothing runs until you confirm the card"
                >
                  <Zap size={11} color={C.magentaSoft} />
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </GlassPanel>
    </div>
  );
}
